// Choosing what goes in a journal.
//
// The sheet shows every entry, already-in ones ticked, and hands back the whole selected set — the
// server diffs it. That is why removing here is safe: unticking an entry sends a set without it,
// which deletes a membership and nothing else. Ported from journals-web.jsx (AddEntriesModal).

import { useMemo, useState } from 'react';
import { JournalSheet } from './JournalSheet';
import { IconCheck, IconFolder, IconSearch } from '../components/Icons';
import { snippet } from '../screens/home-support';
import { entryLabel } from '../entries/EntryRow';
import { shortDate } from '../format/dates';
import type { Entry } from '../api/entries';
import type { Collection } from '../api/collections';

export function AddEntriesSheet({
  collection,
  entries,
  countFor,
  onClose,
  onCommit,
}: {
  collection: Collection;
  /** Everything writable into a journal: the feed, minus anything unfinished. */
  entries: Entry[];
  countFor(entryId: string): number;
  onClose(): void;
  onCommit(entryIds: string[]): void;
}) {
  const [selected, setSelected] = useState<string[]>(() => [...collection.entryIds]);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((entry) =>
      `${entry.title} ${entry.body}`.toLowerCase().includes(needle),
    );
  }, [entries, query]);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((e) => e !== id) : [...current, id],
    );

  const added = selected.filter((id) => !collection.entryIds.includes(id)).length;
  const removed = collection.entryIds.filter((id) => !selected.includes(id)).length;

  return (
    <JournalSheet
      title={
        <>
          Add to <em>{collection.title || 'this journal'}</em>
        </>
      }
      confirmLabel="Done"
      onConfirm={() => onCommit(selected)}
      onClose={onClose}
      footer={
        added || removed
          ? [
              added ? `${added} added` : null,
              removed ? `${removed} removed` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : 'Entries stay in your main feed'
      }
    >
      <div className="csearch">
        <IconSearch />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search all entries…"
          aria-label="Search all entries"
        />
      </div>

      <div className="csheet-scroll">
        {visible.length === 0 ? (
          <div className="coll-empty">
            {entries.length === 0
              ? 'Nothing written yet. Anything you write can be collected here later.'
              : 'No entry matches that.'}
          </div>
        ) : null}

        {visible.map((entry) => {
          const on = selected.includes(entry.id);
          const elsewhere = countFor(entry.id);
          return (
            <button
              key={entry.id}
              className={`cpick ${on ? 'on' : ''}`}
              onClick={() => toggle(entry.id)}
              aria-pressed={on}
            >
              <span className="cbox">{on ? <IconCheck /> : null}</span>
              <span className="cpick-body">
                <span className="cpick-top">
                  <span className="cpick-title">{entryLabel(entry)}</span>
                  {elsewhere > 0 ? (
                    <span className="cpick-in" title={`In ${elsewhere} journals`}>
                      <IconFolder /> {elsewhere}
                    </span>
                  ) : null}
                </span>
                <span className="cpick-sub">
                  {shortDate(entry.createdAt)} · {entry.kind === 'dream' ? 'Dream' : 'Waking'} —{' '}
                  {snippet(entry.body)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </JournalSheet>
  );
}
